import { ref, computed, watch } from "vue";
import { useDashboardTab } from "./useDashboardTab";
import { getMatches } from "@/services/dashboard/dashboard";
import { getSeasonName } from "./seasonUtils";

/**
 * Composable del panel de partidos del dashboard.
 * Tabla paginada y ordenable con datos fusionados entre temporadas.
 * Extiende useDashboardTab para gestión de temporadas y ciclo de vida.
 *
 * @param {import('vue').Ref<number[]>} seasonIdsRef
 * @param {import('vue').Ref<Array>} seasonsRef
 * @returns {{
 *     data: import('vue').Ref<object|null>,
 *     loading: import('vue').Ref<boolean>,
 *     error: import('vue').Ref<string|null>,
 *     isMultiSeason: import('vue').ComputedRef<boolean>,
 *     matchesList: import('vue').ComputedRef<Array>,
 *     pagination: import('vue').ComputedRef<{ current_page: number, per_page: number, total: number, last_page: number }|null>,
 *     sortField: import('vue').Ref<string>,
 *     sortOrder: import('vue').Ref<string>,
 *     currentPage: import('vue').Ref<number>,
 *     perPage: import('vue').Ref<number>,
 *     setSort: (field: string) => void,
 *     goToPage: (page: number) => void,
 *     seasonBreakdown: import('vue').ComputedRef<Array|null>,
 *     refresh: () => Promise<void>,
 * }}
 */
export function useMatches(seasonIdsRef, seasonsRef) {
    // ==================== Estado reactivo (específico de partidos) ====================
    const sortField = ref("revenue");
    const sortOrder = ref("desc");
    const currentPage = ref(1);
    const perPage = ref(15);

    /**
     * Wrapper que incluye los parámetros de paginación/orden en la llamada a getMatches.
     * useDashboardTab llama a fetchFn(seasonIds, signal) internamente.
     */
    function matchesFetch(seasonIds, signal) {
        return getMatches(
            seasonIds,
            {
                sort: sortField.value,
                order: sortOrder.value,
                perPage: perPage.value,
                page: currentPage.value,
            },
            signal,
        );
    }

    // Base: gestiona AbortController, minLoadingTimer, watch de temporadas, onUnmounted
    const base = useDashboardTab(seasonIdsRef, seasonsRef, matchesFetch);

    /**
     * Resetea currentPage cuando cambian las temporadas (antes de que el fetch base se ejecute).
     * {@link useDashboardTab} tiene un watch({ immediate: true }) que dispara fetch.
     * Con flush: 'sync' nos aseguramos de resetear la página ANTES de que el fetch base lea currentPage.
     */
    watch(
        seasonIdsRef,
        () => {
            currentPage.value = 1;
        },
        { flush: "sync" },
    );

    // ==================== Computed ====================

    /**
     * Lista de partidos fusionada entre todas las temporadas.
     * Los partidos vienen paginados por separado por temporada.
     * En multi-season, se muestran todas las páginas 1 de cada temporada juntas.
     */
    const matchesList = computed(() => {
        if (!base.data.value) return [];
        const perSeason = base.data.value?.data?.per_season || {};
        const allMatches = [];
        for (const [, entry] of Object.entries(perSeason)) {
            if (entry?.matches) {
                allMatches.push(...entry.matches);
            }
        }
        return allMatches;
    });

    /**
     * Paginación — usando la primera temporada como referencia.
     * En multi-season, pagination será de la primera temporada.
     */
    const pagination = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};
        const firstEntry = Object.values(perSeason)[0];
        return firstEntry?.pagination || null;
    });

    /**
     * Desglose por temporada.
     */
    const seasonBreakdown = computed(() => {
        if (!base.data.value) return null;
        const perSeason = base.data.value?.data?.per_season || {};
        const entries = Object.entries(perSeason).filter(
            ([, entry]) => entry?.matches?.length,
        );
        if (entries.length === 0) return null;

        return entries.map(([seasonId, entry]) => {
            const name = getSeasonName(seasonsRef.value, seasonId);
            const matches = entry.matches || [];
            const totalRevenue = matches.reduce(
                (sum, m) => sum + (m.total_revenue || 0),
                0,
            );
            const totalAttendance = matches.reduce(
                (sum, m) => sum + (m.total_attendance || 0),
                0,
            );
            return {
                season: name,
                matchCount: matches.length,
                totalRevenue,
                totalAttendance,
            };
        });
    });

    // ==================== Métodos ====================

    function setSort(field) {
        if (sortField.value === field) {
            sortOrder.value = sortOrder.value === "desc" ? "asc" : "desc";
        } else {
            sortField.value = field;
            sortOrder.value = "desc";
        }
        currentPage.value = 1;
        base.fetch(seasonIdsRef.value);
    }

    function goToPage(page) {
        if (page < 1) return;
        currentPage.value = page;
        base.fetch(seasonIdsRef.value);
    }

    /**
     * Partidos agrupados por temporada para mostrar sub-encabezados.
     * Solo usado cuando isMultiSeason es true.
     */
    const groupedBySeason = computed(() => {
        if (!base.data.value) return [];
        const perSeason = base.data.value?.data?.per_season || {};
        const groups = [];
        for (const [seasonId, entry] of Object.entries(perSeason)) {
            if (entry?.matches?.length) {
                const name = getSeasonName(seasonsRef.value, seasonId);
                groups.push({
                    seasonId,
                    seasonName: name,
                    matches: entry.matches,
                });
            }
        }
        return groups;
    });

    return {
        data: base.data,
        loading: base.loading,
        error: base.error,
        isMultiSeason: base.isMultiSeason,
        matchesList,
        pagination,
        sortField,
        sortOrder,
        currentPage,
        perPage,
        setSort,
        goToPage,
        seasonBreakdown,
        groupedBySeason,
        refresh: base.refresh,
    };
}
